import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import {
  IChatbotMultiDeliverable,
  IChatbotMultiKeyword,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.interface';
import { IChatbotDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.interface';
import {
  IDeliverableImage,
  TDeliverable,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IGamePlayDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { IProductPlacementDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.interface';
import { IImageDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/image-deliverable/image-deliverable.interface';
import {
  ITextContent,
  ITextDeliverable,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-deliverable/text-deliverable.interface';
import { ITextDescriptionDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-description-deliverable/text-description-deliverable.interface';
import {
  EVisualFocus,
  IVisualDeliverable,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.interface';
import { TExecutionApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { EAnalyticsStatus, EProcessStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { ICtaAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import {
  IAnalyticsNode,
  IAnalyticsStatusable,
  ICtaResult,
  IExecutionResult,
  IPerformanceStatusable,
  IProcessable,
  TExpectedKpi,
} from '@interfaces/campaign-analytics.interface';
import {
  IAudioTrackingAnalytics,
  IAudioTrackingKeywordFinding,
  ICaptionAnalytics,
  ICaptionKeywordFinding,
  IChatBotAnalytics,
  IChatBotMultiAnalytics,
  IChatBotMultiKeywordAnalytics,
  IContentAnalytics,
  ICustomAnalytics,
  IDescriptionAnalytics,
  IDescriptionKeywordFinding,
  IGamePlayAnalytics,
  IHashTagAnalytics,
  IHashTagKeywordFinding,
  IKeywordAnalytics,
  IMentionAnalytics,
  IMentionKeywordFinding,
  IMessageAnalytics,
  IMessageKeywordFinding,
  IProductPlacementAnalytics,
  IShowcaseAnalytics,
  ITalkingPointAiAnalytics,
  ITalkingPointAnalytics,
  ITalkingPointKeywordFinding,
  ITextTrackingAnalytics,
  ITextTrackingKeywordFinding,
  ITitleAnalytics,
  ITitleKeywordFinding,
  IVisualAnalytics,
} from '@interfaces/deliverable-analytics.interface';
import { IGlobalTracking } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import isNil from 'lodash/isNil';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class SqlToAnalyticsUtilitiesService {
  /**
   * Record forces every `EDeliverableType` to have a SQL mapper.
   * Adding a new enum value without a mapper entry is a compile error.
   */
  private readonly sqlMappersByType: Record<EDeliverableType, (ctaAnalytics: ICtaAnalyticsApi) => TDeliverable[]> = {
    [EDeliverableType.audioTracking]: (c) => this.mapAudioTrackingsToSql(c),
    [EDeliverableType.caption]: (c) => this.mapCaptionsToSql(c),
    [EDeliverableType.chatBot]: (c) => this.mapChatbotsToSql(c),
    [EDeliverableType.chatbotMulti]: (c) => this.mapChatbotMultisToSql(c),
    [EDeliverableType.content]: (c) => this.mapContentsToSql(c),
    [EDeliverableType.custom]: (c) => this.mapCustomsToSql(c),
    [EDeliverableType.description]: (c) => this.mapDescriptionsToSql(c),
    [EDeliverableType.gamePlay]: (c) => this.mapGamePlaysToSql(c),
    [EDeliverableType.hashTag]: (c) => this.mapHashTagsToSql(c),
    [EDeliverableType.mention]: (c) => this.mapMentionsToSql(c),
    [EDeliverableType.message]: (c) => this.mapMessagesToSql(c),
    [EDeliverableType.productPlacement]: (c) => this.mapProductPlacementsToSql(c),
    [EDeliverableType.showcase]: (c) => this.mapShowcasesToSql(c),
    [EDeliverableType.talkingPoint]: (c) => this.mapTalkingPointsToSql(c),
    [EDeliverableType.talkingPointAi]: (c) => this.mapTalkingPointAisToSql(c),
    [EDeliverableType.textTracking]: (c) => this.mapTextTrackingsToSql(c),
    [EDeliverableType.title]: (c) => this.mapTitlesToSql(c),
    [EDeliverableType.visual]: (c) => this.mapVisualsToSql(c),
  };

  public mapCtaResultToSql(
    creator: ICreator,
    ctaResult: ICtaResult | TNil,
    analyticsNode: IAnalyticsNode,
  ): ICtaTemplateApi | null {
    const { emptyCtaAnalytics } = analyticsNode;
    const ctaId = ctaResult?.ctaId ?? analyticsNode.nonExecutedCtaId ?? '';
    const ctaName = ctaResult?.name ?? analyticsNode.nonExecutedCtaName ?? '';

    if (!emptyCtaAnalytics) {
      return null;
    }

    return {
      audioTrackings: this.mapAudioTrackingsToSql(emptyCtaAnalytics),
      campaignId: analyticsNode.campaignId,
      captions: this.mapCaptionsToSql(emptyCtaAnalytics),
      chatbotMultis: this.mapChatbotMultisToSql(emptyCtaAnalytics),
      chatbots: this.mapChatbotsToSql(emptyCtaAnalytics),
      contents: this.mapContentsToSql(emptyCtaAnalytics),
      ctaCreators: [{ creatorId: creator.id, ctaTemplateId: ctaId, id: uuidv4() }],
      customs: this.mapCustomsToSql(emptyCtaAnalytics),
      descriptions: this.mapDescriptionsToSql(emptyCtaAnalytics),
      executions: ctaResult ? this.mapExecutionsToSql(ctaResult.executionResults) : [],
      gamePlays: this.mapGamePlaysToSql(emptyCtaAnalytics),
      hashTags: this.mapHashTagsToSql(emptyCtaAnalytics),
      id: ctaId,
      isAllCreators: false,
      mentions: this.mapMentionsToSql(emptyCtaAnalytics),
      messages: this.mapMessagesToSql(emptyCtaAnalytics),
      name: ctaName,
      platformFeature: analyticsNode.platformFeature,
      productPlacements: this.mapProductPlacementsToSql(emptyCtaAnalytics),
      showcases: this.mapShowcasesToSql(emptyCtaAnalytics),
      talkingPointAis: this.mapTalkingPointAisToSql(emptyCtaAnalytics),
      talkingPoints: this.mapTalkingPointsToSql(emptyCtaAnalytics),
      textTrackings: this.mapTextTrackingsToSql(emptyCtaAnalytics),
      titles: this.mapTitlesToSql(emptyCtaAnalytics),
      visuals: this.mapVisualsToSql(emptyCtaAnalytics),
    };
  }

  public mapExecutionsToSql(executions: IExecutionResult[] | Map<string, IExecutionResult>): TExecutionApi[] {
    const mappedExecutions: TExecutionApi[] = [];

    executions.forEach((execution) => {
      const executionSql = this.mapExecutionToSql(execution);

      if (executionSql) {
        mappedExecutions.push(executionSql);
      }
    });

    return mappedExecutions;
  }

  public mapExecutionToSql(execution: IExecutionResult): TExecutionApi | null {
    if (!execution.ctaExecutionId) {
      return null;
    }

    return {
      duration: execution.plannedDuration ?? null,
      executionCountTarget: execution.executionCountTarget ?? null,
      executionDate: execution.plannedStartDate ?? null,
      executionTime: execution.plannedStartTime ?? null,
      executionType: execution.executionType ?? null,
      expectedKpis: execution.expectedKpis?.map((e) => ({
        executionId: execution.ctaExecutionId!,
        expectedValue: e.expectedValue,
        id: e.expectedKpiId,
        kpi: e.kpi as TExpectedKpi,
      })),
      id: execution.ctaExecutionId,
      name: execution.name,
    };
  }

  public getDeliverablesInSqlFormat(ctaAnalytics: ICtaAnalyticsApi): TDeliverable[] {
    return Object.values(this.sqlMappersByType).flatMap((mapper) => mapper(ctaAnalytics));
  }

  public mapTitlesToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.titles?.map((t) => ({
        contents:
          t.keywords?.map(
            (k): ITextContent => ({
              id: uuidv4(),
              status: t.keywords?.find((keyword) => keyword.keywordId === k.keywordId)?.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: t.deliverableId,
        matchMode: t.matchMode,
        name: t.name,
        sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        type: EDeliverableType.title,
      })) ?? []
    );
  }

  public mapGamePlaysToSql(ctaAnalytics: ICtaAnalyticsApi): IGamePlayDeliverable[] {
    return (
      ctaAnalytics.gamePlay?.map((g) => ({
        boxArtUrl: g.boxArtUrl,
        duration: g.duration,
        gameId: g.gameId,
        gameName: g.gameName,
        id: g.deliverableId,
        name: g.name,
        sponsoredContentRelevant: g.sponsoredContentRelevant ?? true,
        type: EDeliverableType.gamePlay,
      })) ?? []
    );
  }

  public mapChatbotsToSql(ctaAnalytics: ICtaAnalyticsApi): IChatbotDeliverable[] {
    return (
      ctaAnalytics.chatbot?.map((c) => ({
        id: c.deliverableId,
        interval: String(c.interval),
        name: c.name,
        sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
        text: c.text,
        type: EDeliverableType.chatBot,
      })) ?? []
    );
  }

  public mapChatbotMultisToSql(ctaAnalytics: ICtaAnalyticsApi): IChatbotMultiDeliverable[] {
    return (
      ctaAnalytics.chatbotMultis?.map(
        (c: IChatBotMultiAnalytics): IChatbotMultiDeliverable => ({
          id: c.deliverableId,
          keywords: (c.keywords ?? []).map(
            (k): IChatbotMultiKeyword => ({
              id: uuidv4(),
              status: k.status,
              text: k.keyword,
            }),
          ),
          matchMode: c.matchMode,
          maxInterval: !isNil(c.maxInterval) ? String(c.maxInterval) : null,
          minInterval: !isNil(c.minInterval) ? String(c.minInterval) : null,
          name: c.name,
          sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
          type: EDeliverableType.chatbotMulti,
        }),
      ) ?? []
    );
  }

  public mapTalkingPointsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.talkingPoints?.map((t) => ({
        contents:
          t.keywords?.map(
            (to): ITextContent => ({
              id: uuidv4(),
              status: to.status,
              text: to.keyword,
            }),
          ) ?? [],
        id: t.deliverableId,
        matchMode: t.matchMode,
        name: t.name,
        sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        type: EDeliverableType.talkingPoint,
      })) ?? []
    );
  }

  public mapVisualsToSql(ctaAnalytics?: ICtaAnalyticsApi, visual?: IVisualAnalytics): IVisualDeliverable[] {
    return (
      (visual ? [visual] : ctaAnalytics?.visuals)?.map((v) => ({
        focus: v.focus ?? null,
        id: v.deliverableId,
        imageFile: null,
        imageId: v.image,
        name: v.name,
        sponsoredContentRelevant: v.sponsoredContentRelevant ?? true,
        type: EDeliverableType.visual,
      })) ?? []
    );
  }

  public mapProductPlacementsToSql(
    ctaAnalytics?: ICtaAnalyticsApi,
    productPlacement?: IProductPlacementAnalytics,
  ): IProductPlacementDeliverable[] {
    return (
      (productPlacement ? [productPlacement] : ctaAnalytics?.productPlacements)?.map((p) => ({
        id: p.deliverableId,
        images: p.images.map(
          (i): IDeliverableImage => ({
            id: uuidv4(),
            imageId: i,
          }),
        ),
        name: p.name,
        productName: p.productName,
        sponsoredContentRelevant: p.sponsoredContentRelevant ?? true,
        type: EDeliverableType.productPlacement,
      })) ?? []
    );
  }

  public mapShowcasesToSql(ctaAnalytics?: ICtaAnalyticsApi, showcase?: IShowcaseAnalytics): IImageDeliverable[] {
    return (
      (showcase ? [showcase] : ctaAnalytics?.showcases)?.map((s) => ({
        description: s.description,
        id: s.deliverableId,
        images: s.images.map(
          (i): IDeliverableImage => ({
            id: uuidv4(),
            imageId: i,
          }),
        ),
        name: s.name,
        sponsoredContentRelevant: s.sponsoredContentRelevant ?? true,
        type: EDeliverableType.showcase,
      })) ?? []
    );
  }

  public mapCustomsToSql(ctaAnalytics?: ICtaAnalyticsApi, custom?: ICustomAnalytics): IImageDeliverable[] {
    return (
      (custom ? [custom] : ctaAnalytics?.customs)?.map((s) => ({
        description: s.description,
        id: s.deliverableId,
        images: s.images.map(
          (i): IDeliverableImage => ({
            id: uuidv4(),
            imageId: i,
          }),
        ),
        name: s.name,
        sponsoredContentRelevant: s.sponsoredContentRelevant ?? true,
        type: EDeliverableType.custom,
      })) ?? []
    );
  }

  public mapTextTrackingsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.textTrackings?.map((t) => ({
        contents:
          t.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: t.deliverableId,
        matchMode: t.matchMode,
        name: t.name,
        sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        type: EDeliverableType.textTracking,
      })) ?? []
    );
  }

  public mapAudioTrackingsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.audioTrackings?.map((a) => ({
        contents:
          a.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: a.deliverableId,
        matchMode: a.matchMode,
        name: a.name,
        sponsoredContentRelevant: a.sponsoredContentRelevant ?? true,
        type: EDeliverableType.audioTracking,
      })) ?? []
    );
  }

  public mapContentsToSql(ctaAnalytics?: ICtaAnalyticsApi, content?: IContentAnalytics): IImageDeliverable[] {
    return (
      (content ? [content] : ctaAnalytics?.contents)?.map((c) => ({
        description: c.description,
        id: c.deliverableId,
        images: c.images.map(
          (i): IDeliverableImage => ({
            id: uuidv4(),
            imageId: i,
          }),
        ),
        name: c.name,
        sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
        type: EDeliverableType.content,
      })) ?? []
    );
  }

  public mapCaptionsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.captions?.map((c) => ({
        contents:
          c.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: c.deliverableId,
        matchMode: c.matchMode,
        name: c.name,
        sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
        type: EDeliverableType.caption,
      })) ?? []
    );
  }

  public mapHashTagsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.hashtags?.map((h) => ({
        contents:
          h.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: h.deliverableId,
        matchMode: h.matchMode,
        name: h.name,
        sponsoredContentRelevant: h.sponsoredContentRelevant ?? true,
        type: EDeliverableType.hashTag,
      })) ?? []
    );
  }

  public mapMentionsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.mentions?.map((m) => ({
        contents:
          m.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: m.deliverableId,
        matchMode: m.matchMode,
        name: m.name,
        sponsoredContentRelevant: m.sponsoredContentRelevant ?? true,
        type: EDeliverableType.mention,
      })) ?? []
    );
  }

  public mapDescriptionsToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.descriptions?.map((d) => ({
        contents:
          d.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: d.deliverableId,
        matchMode: d.matchMode,
        name: d.name,
        sponsoredContentRelevant: d.sponsoredContentRelevant ?? true,
        type: EDeliverableType.description,
      })) ?? []
    );
  }

  public mapMessagesToSql(ctaAnalytics: ICtaAnalyticsApi): ITextDeliverable[] {
    return (
      ctaAnalytics.messages?.map((m) => ({
        contents:
          m.keywords?.map(
            (k): ITextContent => ({
              id: k.keywordId,
              status: k.status,
              text: k.keyword,
            }),
          ) ?? [],
        id: m.deliverableId,
        matchMode: m.matchMode,
        name: m.name,
        sponsoredContentRelevant: m.sponsoredContentRelevant ?? true,
        type: EDeliverableType.message,
      })) ?? []
    );
  }

  public mapTalkingPointAisToSql(
    ctaAnalytics?: ICtaAnalyticsApi,
    talkingPointAi?: ITalkingPointAiAnalytics,
  ): ITextDescriptionDeliverable[] {
    return (
      (talkingPointAi ? [talkingPointAi] : ctaAnalytics?.talkingPointAis)?.map((t) => ({
        analyticsCurrentCount: t.findingsCount ?? t.findings?.length ?? null,
        analyticsCurrentRatio: t.taskCoverage ?? null,
        id: t.deliverableId,
        name: t.name,
        sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        status: t.statusManual ?? t.status,
        text: t.target,
        type: EDeliverableType.talkingPointAi,
      })) ?? []
    );
  }

  public mapTitlesToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): ITitleAnalytics[] {
    return (
      ctaTemplateWithCustomValues.titles?.map(
        (t): ITitleAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: t.id,
          keywords:
            t.contents?.map(
              (c): IKeywordAnalytics<ITitleKeywordFinding> => ({
                ...this.getEmptyProcessableAndAnalyticsStatusable(),
                findings: [],
                findingsCount: 0,
                keyword: c.text,
                keywordId: uuidv4(),
                previousStatusSet: [],
                status: c.status ?? EAnalyticsStatus.processing,
                target: c.text,
              }),
            ) ?? [],
          matchMode: t.matchMode,
          name: t.name,
          sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapGamePlaysToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IGamePlayAnalytics[] {
    return (
      ctaTemplateWithCustomValues.gamePlays?.map(
        (g): IGamePlayAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          boxArtUrl: '',
          deliverableId: g.id,
          duration: g.duration,
          findings: [],
          findingsCount: 0,
          gameId: String(g.gameId),
          gameName: g.gameName,
          name: g.name,
          sponsoredContentRelevant: g.sponsoredContentRelevant ?? true,
          target: g.name,
          totalPlayTime: 0,
        }),
      ) ?? []
    );
  }

  public mapChatbotMultisToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IChatBotMultiAnalytics[] {
    return (
      ctaTemplateWithCustomValues.chatbotMultis?.map(
        (c): IChatBotMultiAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: c.id,
          findings: [],
          findingsCount: 0,
          keywords: (c.keywords ?? []).map(
            (k): IChatBotMultiKeywordAnalytics => ({
              findingCount: 0,
              keyword: k.text,
              matchedByAny: false,
              missedByAny: false,
              status: k.status ?? EAnalyticsStatus.processing,
            }),
          ),
          matchMode: c.matchMode,
          maxInterval: !isNil(c.maxInterval) && c.maxInterval !== '' ? Number(c.maxInterval) : null,
          minInterval: !isNil(c.minInterval) && c.minInterval !== '' ? Number(c.minInterval) : null,
          name: c.name,
          previousFindingDatetime: null,
          sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
          statusMaxInterval: EAnalyticsStatus.processing,
          statusMinInterval: EAnalyticsStatus.processing,
          statusText: EAnalyticsStatus.processing,
          target: c.name,
        }),
      ) ?? []
    );
  }

  public mapChatbotsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IChatBotAnalytics[] {
    return (
      ctaTemplateWithCustomValues.chatbots?.map(
        (c): IChatBotAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: c.id,
          findings: [],
          findingsCount: 0,
          interval: Number(c.interval),
          name: c.name,
          previousStatusSet: [],
          sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
          statusInterval: EAnalyticsStatus.processing,
          statusText: EAnalyticsStatus.processing,
          target: c.text,
          text: c.text,
        }),
      ) ?? []
    );
  }

  public mapTalkingPointsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): ITalkingPointAnalytics[] {
    return (
      ctaTemplateWithCustomValues.talkingPoints?.map(
        (t): ITalkingPointAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: t.id,
          keywords: t.contents.map(
            (to): IKeywordAnalytics<ITalkingPointKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: to.text,
              keywordId: to.id,
              previousStatusSet: [],
              status: to.status ?? EAnalyticsStatus.processing,
              target: to.text,
            }),
          ),
          matchMode: t.matchMode,
          name: t.name,
          sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapVisualsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IVisualAnalytics[] {
    return (
      ctaTemplateWithCustomValues.visuals?.map(
        (v): IVisualAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: v.id,
          findings: [],
          findingsCount: 0,
          focus: v.focus ?? EVisualFocus.mixed,
          image: v.imageId ?? '',
          name: v.name,
          sponsoredContentRelevant: v.sponsoredContentRelevant ?? true,
          target: v.name,
        }),
      ) ?? []
    );
  }

  public mapProductPlacementsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IProductPlacementAnalytics[] {
    return (
      ctaTemplateWithCustomValues.productPlacements?.map(
        (p): IProductPlacementAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: p.id,
          findings: [],
          findingsCount: 0,
          images: p.images.map((i) => i.imageId ?? ''),
          name: p.name,
          productName: p.productName,
          sponsoredContentRelevant: p.sponsoredContentRelevant ?? true,
          target: p.name,
        }),
      ) ?? []
    );
  }

  public mapShowcasesToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IShowcaseAnalytics[] {
    return (
      ctaTemplateWithCustomValues.showcases?.map(
        (s): IShowcaseAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: s.id,
          description: s.description,
          findings: [],
          findingsCount: 0,
          images: s.images.map((i) => i.imageId ?? ''),
          name: s.name,
          sponsoredContentRelevant: s.sponsoredContentRelevant ?? true,
          target: s.description,
        }),
      ) ?? []
    );
  }

  public mapCustomsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): ICustomAnalytics[] {
    return (
      ctaTemplateWithCustomValues.customs?.map(
        (s): ICustomAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: s.id,
          description: s.description,
          findings: [],
          findingsCount: 0,
          images: s.images.map((i) => i.imageId ?? ''),
          name: s.name,
          sponsoredContentRelevant: s.sponsoredContentRelevant ?? true,
          target: s.description,
        }),
      ) ?? []
    );
  }

  public mapTextTrackingsToAnalytics(
    ctaTemplateWithCustomValues: ICtaTemplateApi | IGlobalTracking,
    globalTracking?: IGlobalTracking,
  ): ITextTrackingAnalytics[] {
    const textTrackings = [
      ...(ctaTemplateWithCustomValues.textTrackings ?? []),
      ...(globalTracking?.textTrackings ?? []),
    ];

    return (
      textTrackings.map(
        (t): ITextTrackingAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: t.id,
          keywords: t.contents.map(
            (te): IKeywordAnalytics<ITextTrackingKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: te.text,
              keywordId: te.id,
              previousStatusSet: [],
              status: te.status ?? EAnalyticsStatus.processing,
              target: te.text,
            }),
          ),
          matchMode: t.matchMode,
          name: t.name,
          oneTimeProcessed: false,
          sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
          trackingState: [],
        }),
      ) ?? []
    );
  }

  public mapAudioTrackingsToAnalytics(
    ctaTemplateWithCustomValues: ICtaTemplateApi,
    globalTracking?: IGlobalTracking,
  ): IAudioTrackingAnalytics[] {
    const audioTrackings = [
      ...(ctaTemplateWithCustomValues.audioTrackings ?? []),
      ...(globalTracking?.audioTrackings ?? []),
    ];

    return (
      audioTrackings.map(
        (a): IAudioTrackingAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: a.id,
          keywords: a.contents.map(
            (au): IKeywordAnalytics<IAudioTrackingKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: au.text,
              keywordId: au.id,
              previousStatusSet: [],
              status: au.status ?? EAnalyticsStatus.processing,
              target: au.text,
            }),
          ),
          matchMode: a.matchMode,
          name: a.name,
          sponsoredContentRelevant: a.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapCaptionsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): ICaptionAnalytics[] {
    return (
      ctaTemplateWithCustomValues.captions?.map(
        (c): ICaptionAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: c.id,
          keywords: c.contents.map(
            (f): IKeywordAnalytics<ICaptionKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: f.text,
              keywordId: f.id,
              previousStatusSet: [],
              status: f.status ?? EAnalyticsStatus.processing,
              target: f.text,
            }),
          ),
          matchMode: c.matchMode,
          name: c.name,
          sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapDescriptionsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IDescriptionAnalytics[] {
    return (
      ctaTemplateWithCustomValues.descriptions?.map(
        (d): IDescriptionAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: d.id,
          keywords: d.contents.map(
            (f): IKeywordAnalytics<IDescriptionKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: f.text,
              keywordId: f.id,
              previousStatusSet: [],
              status: f.status ?? EAnalyticsStatus.processing,
              target: f.text,
            }),
          ),
          matchMode: d.matchMode,
          name: d.name,
          sponsoredContentRelevant: d.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapHashTagsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IHashTagAnalytics[] {
    return (
      ctaTemplateWithCustomValues.hashTags?.map(
        (h): IHashTagAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: h.id,
          keywords: h.contents.map(
            (f): IKeywordAnalytics<IHashTagKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: f.text,
              keywordId: f.id,
              previousStatusSet: [],
              status: f.status ?? EAnalyticsStatus.processing,
              target: f.text,
            }),
          ),
          matchMode: h.matchMode,
          name: h.name,
          sponsoredContentRelevant: h.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapMentionsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IMentionAnalytics[] {
    return (
      ctaTemplateWithCustomValues.mentions?.map(
        (m): IMentionAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: m.id,
          keywords: m.contents.map(
            (f): IKeywordAnalytics<IMentionKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: f.text,
              keywordId: f.id,
              previousStatusSet: [],
              status: f.status ?? EAnalyticsStatus.processing,
              target: f.text,
            }),
          ),
          matchMode: m.matchMode,
          name: m.name,
          sponsoredContentRelevant: m.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapMessagesToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IMessageAnalytics[] {
    return (
      ctaTemplateWithCustomValues.messages?.map(
        (m): IMessageAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: m.id,
          keywords: m.contents.map(
            (f): IKeywordAnalytics<IMessageKeywordFinding> => ({
              ...this.getEmptyProcessableAndAnalyticsStatusable(),
              findings: [],
              findingsCount: 0,
              keyword: f.text,
              keywordId: f.id,
              previousStatusSet: [],
              status: f.status ?? EAnalyticsStatus.processing,
              target: f.text,
            }),
          ),
          matchMode: m.matchMode,
          name: m.name,
          sponsoredContentRelevant: m.sponsoredContentRelevant ?? true,
        }),
      ) ?? []
    );
  }

  public mapContentsToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): IContentAnalytics[] {
    return (
      ctaTemplateWithCustomValues.contents?.map(
        (c): IContentAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: c.id,
          description: c.description,
          findings: [],
          findingsCount: 0,
          images: c.images.map((i) => i.imageId ?? ''),
          name: c.name,
          sponsoredContentRelevant: c.sponsoredContentRelevant ?? true,
          target: c.description,
        }),
      ) ?? []
    );
  }

  public mapTalkingPointAisToAnalytics(ctaTemplateWithCustomValues: ICtaTemplateApi): ITalkingPointAiAnalytics[] {
    return (
      ctaTemplateWithCustomValues.talkingPointAis?.map(
        (t): ITalkingPointAiAnalytics => ({
          ...this.getEmptyProcessableAndAnalyticsStatusable(),
          deliverableId: t.id,
          findings: [],
          findingsCount: 0,
          name: t.name,
          sponsoredContentRelevant: t.sponsoredContentRelevant ?? true,
          target: t.text,
          text: t.text,
        }),
      ) ?? []
    );
  }

  private getEmptyProcessableAndAnalyticsStatusable(): Pick<IProcessable, 'processStatus'> &
    IAnalyticsStatusable &
    IPerformanceStatusable {
    return {
      performanceStatus: {
        costs: 0,
      },
      processStatus: EProcessStatus.notStarted,
      status: EAnalyticsStatus.processing,
    };
  }
}
